<script setup>
import { computed } from 'vue';

const props = defineProps({
  alteration: {
    type: Object,
    required: true,
  },
  currentValue: {
    type: [String, Number, Object, Array, Boolean],
    default: null,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  systemLabels: {
    type: Array,
    default: () => [],
  },
  systemTeams: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['toggle']);

// Computed properties
const fieldLabel = computed(() => {
  const labelMap = {
    'company_data.name': 'Nome da Empresa',
    'company_data.role': 'Papel do Agente',
    'company_data.support.email': 'Email de Suporte',
    'company_data.support.phone': 'Telefone de Suporte',
    'company_data.support.faqLink': 'Link do FAQ',
    'company_data.website': 'Website',
    'company_data.description': 'Descrição da Empresa',
    'company_data.cnpj': 'CNPJ',
    'company_data.policies': 'Políticas',
    'company_data.locations': 'Localizações',
    'company_data.tagFields': 'Campos de Tag',
    'company_data.teamFields': 'Campos de Time',
    'company_data.additionalInfo': 'Informações Adicionais',
    'company_data.humanRedirectConditions': 'Condições de Redirecionamento',
    'company_data.humanAvailabilityMessagingEnabled': 'Mensagens de Disponibilidade Humana',
    'company_data.humanAvailabilityHours': 'Horários de Disponibilidade Humana',
    'company_data.customImageInstructionsEnabled': 'Instruções Personalizadas de Imagem',
    'company_data.imagePrompt': 'Prompt de Imagem',
    'company_data.randomness': 'Aleatoriedade',
    'company_data.audioEnabled': 'Áudio Habilitado',
    'company_data.messageGrouping': 'Agrupamento de Mensagens',
    // 'company_data.schedulingSettings' removido - gerenciado no profissional
    'customer_data': 'Dados do Cliente',
    'name': 'Nome',
    'voice': 'Voz',
    'personality': 'Personalidade',
    'info.personality': 'Personalidade',
    'flow': 'Fluxo',
    'info.flow': 'Fluxo',
    'website': 'Website',
    'description': 'Descrição',
    'role': 'Papel',
  };

  // Para arrays, mostrar apenas o label base sem itemName
  if (props.alteration.itemName && props.alteration.variable.includes('locations')) {
    return 'Localização';
  }
  
  // Para customer_data, usar o name do campo em vez da key
  if (props.alteration.variable === 'customer_data' && props.alteration.itemName) {
    // Tentar buscar o campo atual para pegar o name
    if (props.currentValue && props.currentValue.name) {
      return `${labelMap[props.alteration.variable]} - ${props.currentValue.name}`;
    }
    // Se não tiver currentValue, tentar extrair do newValue
    else if (props.alteration.action === 'add' && props.alteration.newValue) {
      try {
        const newData = typeof props.alteration.newValue === 'string' 
          ? JSON.parse(props.alteration.newValue) 
          : props.alteration.newValue;
        if (newData && newData.name) {
          return `${labelMap[props.alteration.variable]} - ${newData.name}`;
        }
      } catch (e) {
        // Se falhar, usar itemName
      }
    }
    return `${labelMap[props.alteration.variable]} - ${props.alteration.itemName}`;
  }
  
  // Se tiver itemName (para outros campos), adicionar ao label
  if (props.alteration.itemName) {
    const baseLabel = labelMap[props.alteration.variable] || props.alteration.variable;
    return `${baseLabel} - ${props.alteration.itemName}`;
  }

  return labelMap[props.alteration.variable] || props.alteration.variable;
});

// Computed para verificar se é uma nova tag ou time
const isNewTagOrTeam = computed(() => {
  if (!props.alteration || !props.alteration.itemName) return false;
  if (props.alteration.action === 'remove') return false;
  
  const variable = props.alteration.variable;
  const itemName = props.alteration.itemName;
  
  // Para tags: verificar se existe no sistema
  if (variable === 'company_data.tagFields') {
    // Normalizar o nome da tag (minúsculo + hífen)
    const normalizedItemName = itemName.toLowerCase().replace(/\s+/g, '-');
    
    // Verificar se existe alguma label no sistema com esse nome
    const existsInSystem = props.systemLabels.some(label => {
      const normalizedLabelName = (label.title || '').toLowerCase().replace(/\s+/g, '-');
      return normalizedLabelName === normalizedItemName;
    });
    
    // É nova se NÃO existe no sistema
    return !existsInSystem;
  }
  
  // Para times: verificar se existe no sistema
  if (variable === 'company_data.teamFields') {
    // Normalizar o nome do time (apenas minúsculo)
    const normalizedItemName = itemName.toLowerCase();
    
    // Verificar se existe algum time no sistema com esse nome
    const existsInSystem = props.systemTeams.some(team => {
      const normalizedTeamName = (team.name || '').toLowerCase();
      return normalizedTeamName === normalizedItemName;
    });
    
    // É novo se NÃO existe no sistema
    return !existsInSystem;
  }
  
  return false;
});

const actionLabel = computed(() => {
  const actionMap = {
    'add': 'Adicionar',
    'remove': 'Remover',
    'alter': 'Alterar',
  };
  return actionMap[props.alteration.action] || props.alteration.action;
});

const actionColor = computed(() => {
  const colorMap = {
    'add': 'green',
    'remove': 'red',
    'alter': 'blue',
  };
  return colorMap[props.alteration.action] || 'gray';
});

const parseValue = (value) => {
  try {
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      return JSON.parse(value);
    }
    return value;
  } catch (e) {
    return value;
  }
};

const displayValue = computed(() => {
  const value = parseValue(props.alteration.newValue);
  
  // Tratamento especial para campos que podem vir como string simples
  if (typeof value === 'string' && props.alteration.itemName) {
    // Para additionalInfo e policies - estrutura {name, value}
    if (props.alteration.variable === 'company_data.additionalInfo' ||
        props.alteration.variable === 'company_data.policies') {
      return {
        name: props.alteration.itemName,
        value: value
      };
    }
    // Para tagFields e teamFields - estrutura {name, description}
    else if (props.alteration.variable === 'company_data.tagFields' ||
             props.alteration.variable === 'company_data.teamFields') {
      return {
        name: props.alteration.itemName,
        description: value
      };
    }
  }
  
  return value;
});

const currentDisplayValue = computed(() => {
  return props.currentValue;
});

// Método para formatar valores especiais
const normalizeDisplayValue = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim().toLowerCase();
  if (!trimmed) return null;
  return trimmed
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
};

const enumDisplayMaps = {
  voice: {
    male: 'masculina',
    masculina: 'masculina',
    masculino: 'masculina',
    female: 'feminina',
    feminina: 'feminina',
    feminino: 'feminina'
  },
  personality: {
    '1': 'amigavel',
    amigavel: 'amigavel',
    profissional: 'profissional',
    consultiva: 'consultiva',
    '2': 'profissional',
    '3': 'consultiva'
  },
  flow: {
    '1': 'qualificação SDR',
    qualificacao: 'qualificação SDR',
    'qualificacao sdr': 'qualificação SDR',
    '2': 'vendas',
    vendas: 'vendas',
    '3': 'agendamento',
    agendamento: 'agendamento'
  }
};

const getEnumDisplayKey = (variable) => {
  if (variable === 'info.flow') return 'flow';
  if (variable === 'info.personality') return 'personality';
  if (variable === 'voice' || variable === 'flow' || variable === 'personality') {
    return variable;
  }
  return null;
};

const formatEnumDisplay = (value, variable) => {
  const key = getEnumDisplayKey(variable);
  if (!key) return null;
  const normalized = normalizeDisplayValue(value);
  if (!normalized) return null;
  return enumDisplayMaps[key]?.[normalized] || null;
};

const formatSpecialValue = (value, variable) => {
  const enumDisplay = formatEnumDisplay(value, variable);
  if (enumDisplay) {
    return enumDisplay;
  }

  // Para humanAvailabilityHours
  if (variable === 'company_data.humanAvailabilityHours') {
    if (!value) return 'Não configurado';
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (e) {
        return value;
      }
    }
    return formatHumanAvailabilityHours(value);
  }

  // Para campos booleanos
  if (variable === 'company_data.humanAvailabilityMessagingEnabled' ||
      variable === 'company_data.customImageInstructionsEnabled' ||
      variable === 'company_data.audioEnabled') {
    if (typeof value === 'boolean') {
      return value ? 'Habilitado' : 'Desabilitado';
    }
    if (value === 'true' || value === true) return 'Habilitado';
    if (value === 'false' || value === false) return 'Desabilitado';
    return value;
  }

  // Para imagePrompt e humanRedirectConditions - textos longos
  if (variable === 'company_data.imagePrompt' ||
      variable === 'company_data.humanRedirectConditions') {
    if (!value) return 'Não definido';
    return value;
  }

  return null; // Retorna null para usar o tratamento padrão
};

// Methods

const formatFieldLabel = (key) => {
  const labelMap = {
    'name': 'Nome',
    'value': 'Valor',
    'description': 'Descrição',
    'cep': 'CEP',
    'street': 'Logradouro',
    'number': 'Número',
    'complement': 'Complemento',
    'neighborhood': 'Bairro',
    'city': 'Cidade',
    'state': 'Estado',
    'required': 'Obrigatório',
    'email': 'E-mail',
    'phone': 'Telefone',
    'faqLink': 'Link FAQ',
    'timeZone': 'Fuso Horário',
    'workingHours': 'Horário de Funcionamento',
    'timeSlots': 'Horários'
  };
  
  return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
};

// Mapeamento global de dias da semana - usado em múltiplas funções
const daysMap = {
  'sunday': 'Domingo',
  'monday': 'Segunda-feira',
  'tuesday': 'Terça-feira',
  'wednesday': 'Quarta-feira',
  'thursday': 'Quinta-feira',
  'friday': 'Sexta-feira',
  'saturday': 'Sábado',
  'Sunday': 'Domingo',
  'Monday': 'Segunda-feira',
  'Tuesday': 'Terça-feira',
  'Wednesday': 'Quarta-feira',
  'Thursday': 'Quinta-feira',
  'Friday': 'Sexta-feira',
  'Saturday': 'Sábado'
};

// Ordem padrão dos dias da semana
const daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const formatHumanAvailabilityHours = (hours) => {
  if (!hours) return '<span class="text-n-slate-10">Não configurado</span>';

  // Lista dos dias com horários - igual ao AgentDetailView
  let result = '<div class="space-y-2">';

  daysOrder.forEach(dayKey => {
    result += '<div class="flex items-center gap-3">';
    result += '<div class="w-32">';
    result += `<span class="text-sm font-medium text-n-slate-12">${daysMap[dayKey]}:</span>`;
    result += '</div>';

    const dayData = hours[dayKey];
    if (dayData && dayData.enabled && dayData.timeSlots && dayData.timeSlots.length > 0) {
      result += '<div class="flex-1">';
      result += '<div class="flex flex-wrap gap-2">';
      dayData.timeSlots.forEach(slot => {
        const start = slot.startTime || slot.start || '';
        const end = slot.endTime || slot.end || '';
        if (start && end) {
          result += `<span class="text-sm bg-n-brand/10 text-n-brand px-2 py-1 rounded">`;
          result += `${start} - ${end}`;
          result += '</span>';
        }
      });
      result += '</div>';
      result += '</div>';
    } else {
      result += '<div class="text-sm text-n-slate-10 italic">';
      result += 'Sem expediente';
      result += '</div>';
    }

    result += '</div>';
  });

  result += '</div>';

  return result;
};

const formatWorkingHours = (workingHours) => {
  if (!workingHours) return '';

  let result = '<div class="space-y-1">';
  
  // Se workingHours é um array
  if (Array.isArray(workingHours)) {
    workingHours.forEach(dayData => {
      if (dayData && dayData.day) {
        const dayName = daysMap[dayData.day] || dayData.day;
        if (dayData.enabled) {
          const slots = dayData.timeSlots || [];
          if (slots.length > 0) {
            const times = slots.map(slot => {
              const start = slot.start || slot.startTime || '';
              const end = slot.end || slot.endTime || '';
              return `${start} - ${end}`;
            }).filter(time => time !== ' - ').join(', ');
            if (times) {
              result += `<div><strong>${dayName}:</strong> ${times}</div>`;
            } else {
              result += `<div><strong>${dayName}:</strong> Habilitado</div>`;
            }
          } else {
            result += `<div><strong>${dayName}:</strong> Habilitado</div>`;
          }
        } else {
          result += `<div><strong>${dayName}:</strong> Fechado</div>`;
        }
      }
    });
  } 
  // Se workingHours é um objeto
  else if (typeof workingHours === 'object') {
    // Percorre os dias na ordem padrão, verificando ambas as variações (minúscula e capitalizada)
    daysOrder.forEach(day => {
      const dayData = workingHours[day];
      if (dayData && typeof dayData === 'object') {
        const dayName = daysMap[day] || day;
        if (dayData.enabled) {
          const slots = dayData.timeSlots || [];
          if (slots.length > 0) {
            const times = slots.map(slot => {
              const start = slot.start || slot.startTime || '';
              const end = slot.end || slot.endTime || '';
              return `${start} - ${end}`;
            }).filter(time => time !== ' - ').join(', ');
            if (times) {
              result += `<div><strong>${dayName}:</strong> ${times}</div>`;
            } else {
              result += `<div><strong>${dayName}:</strong> Habilitado</div>`;
            }
          } else {
            result += `<div><strong>${dayName}:</strong> Habilitado</div>`;
          }
        } else {
          result += `<div><strong>${dayName}:</strong> Fechado</div>`;
        }
      }
    });
  }
  
  result += '</div>';
  return result;
};

const getObjectFields = (obj) => {
  if (!obj || typeof obj !== 'object') return [];
  
  // Tratamento especial para objetos com estrutura {name, value} ou {name, description}
  // Usado em additionalInfo, policies (name/value) e tagFields, teamFields (name/description)
  if (obj.hasOwnProperty('name') && (obj.hasOwnProperty('value') || obj.hasOwnProperty('description')) && 
      Object.keys(obj).length <= 3) {
    const fields = [];
    
    // Adicionar o nome
    if (obj.name) {
      fields.push({ label: 'Nome', value: String(obj.name) });
    }
    
    // Adicionar o valor (para additionalInfo e policies)
    if (obj.value !== null && obj.value !== undefined) {
      fields.push({ label: 'Valor', value: String(obj.value) });
    }
    
    // Adicionar descrição (para tagFields e teamFields)
    if (obj.description !== null && obj.description !== undefined) {
      fields.push({ label: 'Descrição', value: String(obj.description) });
    }
    
    return fields;
  }
  
  const fields = [];
  
  // Campos que devem ser ocultados (chaves internas)
  const hiddenFields = ['key', 'complementError'];
  
  // Ordem preferencial para campos conhecidos
  const fieldOrder = [
    'name', 'value', 'description', 
    'street', 'number', 'complement', 'neighborhood', 'city', 'state', 'cep',
    'required', 'email', 'phone', 'faqLink', 'timeZone', 'workingHours'
  ];
  
  // Adicionar campos na ordem preferencial
  fieldOrder.forEach(key => {
    if (!hiddenFields.includes(key) && obj.hasOwnProperty(key) && obj[key] !== null && obj[key] !== undefined) {
      const value = obj[key];
      
      // Pular valores vazios, exceto booleanos
      if (value === '' || (Array.isArray(value) && value.length === 0)) {
        return;
      }
      
      if (key === 'required') {
        fields.push({ label: formatFieldLabel(key), value: value ? 'Sim' : 'Não' });
      } else if (key === 'workingHours' && typeof value === 'object') {
        // Tratamento especial para workingHours
        fields.push({ label: formatFieldLabel(key), value: formatWorkingHours(value), isHtml: true });
      } else if (key === 'timeSlots' && Array.isArray(value)) {
        // timeSlots é um array de objetos com startTime e endTime
        if (value.length > 0) {
          const times = value.map(slot => `${slot.startTime || slot.start} - ${slot.endTime || slot.end}`).join(', ');
          fields.push({ label: formatFieldLabel(key), value: times });
        }
      } else if (Array.isArray(value)) {
        // Para outros arrays
        if (value.length > 0) {
          fields.push({ label: formatFieldLabel(key), value: `${value.length} item(s)` });
        }
      } else if (typeof value === 'object') {
        // Para outros objetos, converter para JSON formatado
        fields.push({ label: formatFieldLabel(key), value: JSON.stringify(value, null, 2) });
      } else {
        fields.push({ label: formatFieldLabel(key), value: String(value) });
      }
    }
  });
  
  // Adicionar campos não listados na ordem
  Object.entries(obj).forEach(([key, value]) => {
    if (!hiddenFields.includes(key) && !fieldOrder.includes(key) && value !== null && value !== undefined && value !== '') {
      if (typeof value === 'object' && !Array.isArray(value)) {
        fields.push({ label: formatFieldLabel(key), value: JSON.stringify(value, null, 2) });
      } else if (Array.isArray(value) && value.length > 0) {
        fields.push({ label: formatFieldLabel(key), value: `${value.length} item(s)` });
      } else if (typeof value !== 'object') {
        fields.push({ label: formatFieldLabel(key), value: String(value) });
      }
    }
  });
  
  return fields;
};

const handleToggle = () => {
  emit('toggle', props.alteration);
};
</script>

<template>
  <div 
    class="bg-white dark:bg-slate-900 border rounded-lg p-4 mb-3 cursor-pointer transition-all duration-200"
    :class="{ 
      'border-n-brand bg-n-brand/5': selected,
      'border-n-slate-3 hover:border-n-slate-6': !selected 
    }"
    @click="handleToggle"
  >
    <div class="flex items-center gap-3 mb-3">
      <div class="flex-shrink-0">
        <input
          type="checkbox"
          :checked="selected"
          @click.stop
          @change="handleToggle"
          class="w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600 accent-n-brand"
        />
      </div>
      
      <div class="flex-1 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">{{ fieldLabel }}</h4>
          <!-- Badge para nova tag/time -->
          <span 
            v-if="isNewTagOrTeam"
            class="text-xs font-medium px-2 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11 border border-n-amber-6"
          >
            <i class="i-lucide-sparkles text-xs mr-1"></i>
            {{ alteration.variable === 'company_data.tagFields' ? 'Nova Tag' : 'Novo Time' }} - Será criado no sistema
          </span>
        </div>
        <span 
          class="text-sm font-medium px-2 py-1 rounded"
          :class="{
            'bg-n-brand/10 text-n-brand': actionColor === 'blue',
            'bg-n-teal-3 text-n-teal-11': actionColor === 'green',
            'bg-n-ruby-3 text-n-ruby-11': actionColor === 'red'
          }"
        >
          {{ actionLabel }}
        </span>
      </div>
    </div>
    
    <div class="<!--ml-7-->">
      <!-- Mensagem informativa para novas tags/times -->
      <div v-if="isNewTagOrTeam" class="mb-3 p-2 bg-n-amber-1 border border-n-amber-3 rounded-md">
        <p class="text-xs text-n-amber-11 flex items-center">
          <i class="i-lucide-info-circle mr-1"></i>
          {{ alteration.variable === 'company_data.tagFields' 
            ? 'Esta tag ainda não existe no sistema e será criada automaticamente ao aplicar as alterações. Após a criação, você pode configurá-la em Configurações > Etiquetas.' 
            : 'Este time ainda não existe no sistema e será criado automaticamente ao aplicar as alterações. Após a criação, você pode configurá-lo em Configurações > Times.' }}
        </p>
      </div>
      
      <div v-if="alteration.action === 'alter'">
        <!-- Tratamento especial para campos novos -->
        <template v-if="alteration.variable === 'company_data.humanAvailabilityHours' ||
                       alteration.variable === 'company_data.humanAvailabilityMessagingEnabled' ||
                       alteration.variable === 'company_data.customImageInstructionsEnabled' ||
                       alteration.variable === 'company_data.imagePrompt'">
          <div class="flex items-start gap-4">
            <div class="flex-1" v-if="currentDisplayValue !== null && currentDisplayValue !== undefined">
              <span class="block text-xs font-medium text-n-slate-11 mb-2">Valor Atual:</span>
              <div
                v-if="alteration.variable === 'company_data.humanAvailabilityHours'"
                class="p-3 bg-n-slate-1 rounded-lg text-sm text-n-slate-12 break-words"
                v-html="formatSpecialValue(currentDisplayValue, alteration.variable)"
              ></div>
              <div v-else class="p-3 bg-n-slate-1 rounded-lg text-sm text-n-slate-12 break-words">
                {{ formatSpecialValue(currentDisplayValue, alteration.variable) || currentDisplayValue }}
              </div>
            </div>

            <div class="flex items-center pt-6" v-if="currentDisplayValue !== null && currentDisplayValue !== undefined">
              <i class="i-lucide-arrow-right text-n-slate-11"></i>
            </div>

            <div class="flex-1">
              <span class="block text-xs font-medium text-n-slate-11 mb-2">Novo Valor:</span>
              <div
                v-if="alteration.variable === 'company_data.humanAvailabilityHours'"
                class="p-3 bg-n-brand/10 rounded-lg text-sm text-n-slate-12 break-words"
                v-html="formatSpecialValue(displayValue, alteration.variable)"
              ></div>
              <div v-else class="p-3 bg-n-brand/10 rounded-lg text-sm text-n-slate-12 break-words">
                {{ formatSpecialValue(displayValue, alteration.variable) || displayValue }}
              </div>
            </div>
          </div>
        </template>

        <!-- Para outros objetos complexos -->
        <template v-else-if="typeof displayValue === 'object' && !Array.isArray(displayValue)">
          <div class="grid grid-cols-1" :class="{ 'md:grid-cols-2 gap-4': currentDisplayValue }">
            <div v-if="currentDisplayValue">
              <span class="block text-xs font-medium text-n-slate-11 mb-2">Valor Atual:</span>
              <div class="p-3 bg-n-slate-1 rounded-lg text-sm text-n-slate-12">
                <div v-if="typeof currentDisplayValue === 'object'" class="space-y-2">
                  <div v-for="field in getObjectFields(currentDisplayValue)" :key="field.label" class="flex gap-2 min-w-0">
                    <span class="font-medium text-n-slate-11 whitespace-nowrap">{{ field.label }}:</span>
                    <span v-if="field.isHtml" class="text-n-slate-12 break-words flex-1 min-w-0" v-html="field.value"></span>
                    <span v-else class="text-n-slate-12 break-words flex-1 min-w-0">{{ field.value }}</span>
                  </div>
                </div>
                <span v-else class="break-words">{{ currentDisplayValue }}</span>
              </div>
            </div>
            
            <div>
              <span class="block text-xs font-medium text-n-slate-11 mb-2">Novo Valor:</span>
              <div class="p-3 bg-n-brand/10 rounded-lg text-sm text-n-slate-12">
                <div v-if="typeof displayValue === 'object'" class="space-y-2">
                  <div v-for="field in getObjectFields(displayValue)" :key="field.label" class="flex gap-2 min-w-0">
                    <span class="font-medium text-n-slate-11 whitespace-nowrap">{{ field.label }}:</span>
                    <span v-if="field.isHtml" class="text-n-slate-12 break-words flex-1 min-w-0" v-html="field.value"></span>
                    <span v-else class="text-n-slate-12 break-words flex-1 min-w-0">{{ field.value }}</span>
                  </div>
                </div>
                <span v-else class="break-words">{{ displayValue }}</span>
              </div>
            </div>
          </div>
        </template>
        
        <!-- Para valores simples -->
        <template v-else>
          <div class="flex items-start gap-4" v-if="currentDisplayValue !== null || displayValue !== null">
            <div class="flex-1" v-if="currentDisplayValue !== null && currentDisplayValue !== undefined">
              <span class="block text-xs font-medium text-n-slate-11 mb-2">Valor Atual:</span>
              <div
                v-if="formatSpecialValue(currentDisplayValue, alteration.variable) && alteration.variable === 'company_data.humanAvailabilityHours'"
                class="p-3 bg-n-slate-1 rounded-lg text-sm text-n-slate-12 break-words"
                v-html="formatSpecialValue(currentDisplayValue, alteration.variable)"
              ></div>
              <div v-else class="p-3 bg-n-slate-1 rounded-lg text-sm text-n-slate-12 break-words">
                {{ formatSpecialValue(currentDisplayValue, alteration.variable) || currentDisplayValue }}
              </div>
            </div>

            <div class="flex items-center pt-6" v-if="currentDisplayValue !== null && currentDisplayValue !== undefined">
              <i class="i-lucide-arrow-right text-n-slate-11"></i>
            </div>

            <div class="flex-1">
              <span class="block text-xs font-medium text-n-slate-11 mb-2">Novo Valor:</span>
              <div
                v-if="formatSpecialValue(displayValue, alteration.variable) && alteration.variable === 'company_data.humanAvailabilityHours'"
                class="p-3 bg-n-brand/10 rounded-lg text-sm text-n-slate-12 break-words"
                v-html="formatSpecialValue(displayValue, alteration.variable)"
              ></div>
              <div v-else class="p-3 bg-n-brand/10 rounded-lg text-sm text-n-slate-12 break-words">
                {{ formatSpecialValue(displayValue, alteration.variable) || displayValue }}
              </div>
            </div>
          </div>
        </template>
      </div>
      
      <div v-else-if="alteration.action === 'add'">
        <span class="block text-xs font-medium text-n-slate-11 mb-2">Valor a Adicionar:</span>
        <div class="p-3 bg-n-teal-1 rounded-lg text-sm text-n-slate-12">
          <div v-if="typeof displayValue === 'object' && !Array.isArray(displayValue)" class="space-y-2">
            <div v-for="field in getObjectFields(displayValue)" :key="field.label" class="flex gap-2 min-w-0">
              <span class="font-medium text-n-slate-11 whitespace-nowrap">{{ field.label }}:</span>
              <span v-if="field.isHtml" class="text-n-slate-12 break-words flex-1 min-w-0" v-html="field.value"></span>
              <span v-else class="text-n-slate-12 break-words flex-1 min-w-0">{{ field.value }}</span>
            </div>
          </div>
          <span v-else class="break-words">{{ displayValue }}</span>
        </div>
      </div>
      
      <div v-else-if="alteration.action === 'remove'">
        <span class="block text-xs font-medium text-n-slate-11 mb-2">Valor a Remover:</span>
        <div class="p-3 bg-n-ruby-1 rounded-lg text-sm text-n-slate-12">
          <div v-if="typeof currentDisplayValue === 'object' && !Array.isArray(currentDisplayValue)" class="space-y-2">
            <div v-for="field in getObjectFields(currentDisplayValue)" :key="field.label" class="flex gap-2 min-w-0">
              <span class="font-medium text-n-slate-11 whitespace-nowrap">{{ field.label }}:</span>
              <span v-if="field.isHtml" class="text-n-slate-12 break-words flex-1 min-w-0" v-html="field.value"></span>
              <span v-else class="text-n-slate-12 break-words flex-1 min-w-0">{{ field.value }}</span>
            </div>
          </div>
          <span v-else class="break-words">{{ currentDisplayValue || 'Vazio' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Word breaking utilities for long strings */
.break-words {
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: normal;
}

/* Ensure flex items can shrink and break words */
.min-w-0 {
  min-width: 0;
}

/* Ensure containers don't overflow */
.flex-1 {
  flex: 1 1 0%;
  min-width: 0;
}
</style>
